import React, { useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import FormAutocomplete from "./FormAutocomplete";
import { formButtonSx } from "../common/formFieldStyles";
import colors from "../../styles/colors";
import { COLORS } from "../../styles/uiConstants";

/**
 * Dropdown ile tek seçim + altta eklenen öğeler chip olarak.
 * Besin listesi, tanı listesi vb. tüm menülerde ortak kullanım.
 *
 * @param {string} [emptyText] - Opt-in: liste boşken chip alanında gösterilecek
 *   soluk metin (ör. "Henüz tanı eklenmedi"). Verilmezse boş alan çizilir.
 */
export default function ChipListSelect({
  label,
  options = [],
  value = [],
  onChange,
  getOptionLabel = (opt) => opt?.name ?? "",
  getOptionId = (opt) => opt?._id ?? opt?.id,
  readOnly = false,
  placeholder = "Ekle",
  chipSx = {},
  emptyText,
  addLabel = "Ekle", // opt-in: ekle butonunun metni (ör. "Tanı Ekle")
}) {
  const [selected, setSelected] = useState(null);
  const valueIds = new Set((value || []).map((v) => String(getOptionId(v))));
  const availableOptions = (options || []).filter(
    (opt) => !valueIds.has(String(getOptionId(opt)))
  );

  const handleAdd = () => {
    if (!selected) return;
    if (valueIds.has(String(getOptionId(selected)))) return;
    onChange([...(value || []), selected]);
    setSelected(null);
  };

  const handleRemove = (index) => {
    onChange((value || []).filter((_, i) => i !== index));
  };

  const defaultChipSx = {
    fontWeight: 700,
    fontSize: "0.9rem",
    py: 1.25,
    px: 1.5,
    borderRadius: 2,
    border: "1px solid",
    borderColor: colors.primaryLight,
    bgcolor: colors.chipPatientBg,
    color: colors.chipPatientText,
    "& .MuiChip-deleteIcon": readOnly ? { display: "none" } : {
      color: colors.primary,
      "&:hover": { color: colors.primaryDark },
    },
    "&:hover": readOnly ? {} : {
      bgcolor: colors.primaryLighter,
      borderColor: colors.primary,
    },
    ...chipSx,
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: COLORS.slate500, fontWeight: 700, mb: 1.5 }}>
        {label}
      </Typography>
      {!readOnly && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
          <FormAutocomplete
            label={placeholder}
            options={availableOptions}
            value={selected}
            onChange={(_, newVal) => setSelected(newVal)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(a, b) => getOptionId(a) === getOptionId(b)}
            sx={{ flex: "1 1 200px", minWidth: 0 }}
          />
          <Button
            variant="outlined"
            onClick={handleAdd}
            disabled={!selected}
            sx={{ ...formButtonSx, fontWeight: 900, textTransform: "none", flexShrink: 0 }}
          >
            {addLabel}
          </Button>
        </Box>
      )}
      <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 1.25 }}>
        {emptyText && !(value || []).length ? (
          <Typography sx={{ fontSize: "0.8125rem", color: COLORS.slate400 }}>{emptyText}</Typography>
        ) : null}
        {(value || []).map((item, idx) => (
          <Chip
            key={`${getOptionId(item)}-${idx}`}
            label={getOptionLabel(item)}
            size="small"
            onDelete={readOnly ? undefined : () => handleRemove(idx)}
            sx={defaultChipSx}
          />
        ))}
      </Box>
    </Box>
  );
}
